import merge from 'lodash.merge';

/**
 * Create providers environment initial configuration object
 * @note Add "environment" property into each provider configuration object as it's required later in the application
 * @param {string} environment Name of the created environment (eg. local, dev, stage, prod)
 * @param {object} providersObject Object with providers and it's per-environment configuration
 * @returns {object}
 */
export const createConfig = (environment, providersObject) => {
	const providersEntries = Object.entries(providersObject).map(([providerName, providerConfig]) => [providerName, {
		...providerConfig,
		environment
	}]);
	return Object.fromEntries(providersEntries);
};

/**
 * Create providers environment object dynamically based on requested configuration from "env" parameter
 * @note Should be used only when developer mode is active
 * @param {string} requestedEnvironment Value from the "env" parameter
 * @param {object} env Environment configuration object
 * @param {object} env.providers Provider configuration object
 * @param {string} env.providersEnvironment Providers environment default value
 * @param {object} env.environmentConfigurations Providers config per app environment
 * @returns {object}
 */
export const createObject = (requestedEnvironment, env = {}) => {
	// If requested environment contains only environment name, return requested environment object
	if (!requestedEnvironment || /^(local|dev|stage|prod)$/.test(requestedEnvironment)) {
		return merge({}, env.providers, env.environmentConfigurations[requestedEnvironment || env.providersEnvironment].providers);
	}
	// If requested environment contains variable environment options, handle the combinations
	return Object.keys(env.providers).reduce((object, providerName) => {
		const providerEnvironmentPattern = new RegExp(`${providerName}-(local|dev|stage|prod)`);
		const [, providerEnvironment] = requestedEnvironment.match(providerEnvironmentPattern) || [null, env.providersEnvironment];
		const providerEnvironmentObject = merge({}, env.providers, env.environmentConfigurations[providerEnvironment].providers);
		return {
			...object,
			[providerName]: providerEnvironmentObject[providerName]
		};
	}, {});
};

/**
 * Create test order providers environment ("env" query parameter) string dynamically based on requested cart provider
 * @note Should be used only when developer mode and test order is active
 * @param {string} requestedProvider Value from the "provider" parameter
 * @param {object} env Environment configuration object
 * @param {object} env.providers Provider configuration object
 * @param {string} env.providersEnvironment Providers environment default value
 * @param {object} env.environmentConfigurations Providers config per app environment
 * @returns {string}
 */
export const createString = (requestedProvider = 'gen', env = {}) => {
	return {
		'gen-stage': 'my-dev'
	}[`${requestedProvider}-${env.providersEnvironment}`] || env.providersEnvironment;
};
